// generated by the plop

import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { PROGRESS_STATUSES } from 'symbols';
import { OrdersService } from '../../services/orders.service';
import { OrderModel, OrderStatus } from '../../symbols';
import {
  AcceptedCountWS,
  ClearScrollPositionOrderList,
  CreatedOrderByWS,
  GetOrderList,
  GetOrderListError,
  GetOrderListInteractive,
  GetOrderListSuccess,
  GetOrdersCount,
  GetOrderStatusList,
  GetOrderStatusListError,
  GetOrderStatusListSuccess,
  OrdersAcceptedCountWS,
  OrdersCountWs,
  SetLastOpenedOrderId,
  UpdatedOrderByWS,
  UpdateScrollPositionOrderList
} from './order-list.actions';
import { ORDER_LIST_STATE_DEFAULT, OrderListStateModel } from './order-list.model';

@State({ name: 'OrderList', defaults: ORDER_LIST_STATE_DEFAULT })
@Injectable()
export class OrderListState {

  constructor(private ordersService: OrdersService, private store: Store) {
  }

  @Selector()
  static orderStatusList(state: OrderListStateModel): OrderListStateModel['orderStatusList'] {
    return state.orderStatusList;
  }

  @Selector()
  static groupedOrdersByMasterId(state: OrderListStateModel): { masterId: string, orders: OrderModel[] }[] {
    const orders = state.orderListInteractive?.data || [];

    const groupedMap: { [masterId: string]: OrderModel[] } = {};

    for (const order of orders) {
      if (!order.masterId) {
        continue;
      }
      if (!groupedMap[order.masterId]) {
        groupedMap[order.masterId] = [];
      }
      groupedMap[order.masterId].push(order);
    }
    return Object.entries(groupedMap).map(([masterId, orders]) => ({
      masterId,
      orders
    }));
  }


  @Selector()
  static ordersCount(state: OrderListStateModel): OrderListStateModel['ordersCount'] {
    return state.ordersCount;
  }

  @Selector()
  static orderList(state: OrderListStateModel): OrderListStateModel['orderList'] {
    return state.orderList;
  }

  @Selector()
  static loadOrderListStatus(state: OrderListStateModel): OrderListStateModel['loadOrderListStatus'] {
    return state.loadOrderListStatus;
  }

  @Selector()
  static lastOpenedDocId(state: OrderListStateModel): OrderListStateModel['lastOpenedDocId'] {
    return state.lastOpenedDocId;
  }

  @Selector()
  static scrollPosition(state: OrderListStateModel): OrderListStateModel['scrollPosition'] {
    return state.scrollPosition;
  }

  @Action(GetOrderList)
  getOrderList(ctx: StateContext<OrderListStateModel>, { filter, lazy }: GetOrderList): void {
    if (lazy) {
      const next = ctx.getState().orderList?.links?.next;
      if (next) {
        ctx.patchState({
          loadOrderListStatus: PROGRESS_STATUSES.IN_PROGRESS
        });
        this.ordersService.getOrderListLazy(next).subscribe({
          next: orders => ctx.dispatch(new GetOrderListSuccess(orders, lazy)),
          error: error => ctx.dispatch(new GetOrderListError(error))
        });
      }
    } else {
      ctx.patchState({
        loadOrderListStatus: PROGRESS_STATUSES.IN_PROGRESS
      });
      this.ordersService.getOrderList(filter).subscribe({
        next: orders => ctx.dispatch(new GetOrderListSuccess(orders, lazy)),
        error: error => ctx.dispatch(new GetOrderListError(error))
      });
    }
  }

  @Action(GetOrderListSuccess)
  getOrderListSuccess(ctx: StateContext<OrderListStateModel>, { orderList, lazy }: GetOrderListSuccess): void {
    ctx.patchState({
      loadOrderListStatus: PROGRESS_STATUSES.SUCCESS
    });
    if (lazy) {
      ctx.setState(patch({
        orderList: patch({
          data: append([...orderList.data]),
          links: orderList.links,
          meta: orderList.meta
        })
      }));
    } else {
      ctx.patchState({
        orderList
      });

    }
  }

  @Action(GetOrderListError)
  getOrderListError(ctx: StateContext<OrderListStateModel>, { error }: GetOrderListError): void {
    ctx.patchState({
      loadOrderListStatus: PROGRESS_STATUSES.FAIL
    });
  }

  @Action(GetOrderStatusList)
  getOrderStatusList(ctx: StateContext<OrderListStateModel>): void {
    this.ordersService.getStatusList().subscribe({
      next: orderStatusList => ctx.dispatch(new GetOrderStatusListSuccess(orderStatusList)),
      error: error => ctx.dispatch(new GetOrderStatusListError(error))
    });
  }

  @Action(GetOrderStatusListSuccess)
  getOrderStatusListSuccess(ctx: StateContext<OrderListStateModel>, { orderStatusList }: GetOrderStatusListSuccess): void {
    ctx.patchState({
      orderStatusList
    });
  }

  @Action(GetOrderStatusListError)
  getOrderStatusListError(ctx: StateContext<OrderListStateModel>, { error }: GetOrderStatusListError): void {
    ctx.patchState({
      orderStatusList: []
    });
  }


  @Action(ClearScrollPositionOrderList)
  clearScrollPositionOrderList(ctx: StateContext<OrderListStateModel>): void {
    ctx.patchState({ scrollPosition: 0 });
  }

  @Action(UpdateScrollPositionOrderList)
  updateScrollPosition(ctx: StateContext<OrderListStateModel>, { position }: UpdateScrollPositionOrderList): void {
    ctx.patchState({ scrollPosition: position });
  }

  @Action(SetLastOpenedOrderId)
  setLastOpenedDocId(ctx: StateContext<OrderListStateModel>, { orderId }: SetLastOpenedOrderId): void {
    ctx.patchState({ lastOpenedDocId: orderId });
  }

  @Action(GetOrderListInteractive)
  getOrderListInteractive(ctx: StateContext<OrderListStateModel>, { filter }: GetOrderListInteractive): void {
    ctx.patchState({
      loadOrderListStatus: PROGRESS_STATUSES.IN_PROGRESS
    });
    this.ordersService.getOrderList(filter).subscribe({
      next: orders => {
        ctx.patchState({
          orderListInteractive: orders,
          loadOrderListStatus: PROGRESS_STATUSES.SUCCESS
        });
      },
      error: error => {
        ctx.patchState({
          loadOrderListStatus: PROGRESS_STATUSES.FAIL
        });
      }
    });
  }

  @Action(UpdatedOrderByWS)
  updatedOrderBuWS(ctx: StateContext<OrderListStateModel>, { order }: UpdatedOrderByWS): void {
    const statusIds = this.store.selectSnapshot(OrderListState.orderStatusList);
    const allowedStatusIds = statusIds.filter(status => {
      return [OrderStatus.DONE, OrderStatus.ISSUED, OrderStatus.ISSUED_SELF, OrderStatus.NOT_CONFIRMED, OrderStatus.CANCELLED].includes(status.status);
    }).map(status => status.id);

    const state = ctx.getState();
    const inInteractive = state.orderListInteractive?.data?.find(o => o.id === order.id);
    const inList = state.orderList?.data?.find(o => o.id === order.id);

    const prevStatus = inList?.status?.status ?? inInteractive?.status?.status;
    const newStatus = order.status?.status;
    const countedStatuses = [OrderStatus.ISSUED, OrderStatus.ISSUED_SELF, OrderStatus.NOT_CONFIRMED, OrderStatus.ACCEPTED];
    if (prevStatus !== newStatus && (countedStatuses.includes(prevStatus as OrderStatus) || countedStatuses.includes(newStatus as OrderStatus))) {
      ctx.dispatch(new GetOrdersCount());
    }

    if (allowedStatusIds.includes(order.status?.id)) {
      const isCurrentDay = order.createdAt.toString().split('T')[0] === new Date().toISOString().split('T')[0];

      if (!inInteractive && isCurrentDay) {
        ctx.setState(patch({
          orderListInteractive: patch({
            data: insertItem(order, 0),
            meta: patch({
              totalItems: (state.orderListInteractive?.meta?.totalItems ?? 0) + 1
            })
          })
        }));
      }

      if (!inList) {
        ctx.setState(patch({
          orderList: patch({
            data: insertItem(order, 0),
            meta: patch({
              totalItems: (state.orderList?.meta?.totalItems ?? 0) + 1
            })
          })
        }));
      }
    } else {
      ctx.setState(patch({
        orderList: patch({
          data: removeItem(o => o?.id === order.id)
        }),
        orderListInteractive: patch({
          data: removeItem(o => o?.id === order.id)
        })
      }));
    }

    ctx.setState(patch({
      orderList: patch({
        data: updateItem(o => o?.id === order.id, order)
      }),
      orderListInteractive: patch({
        data: updateItem(o => o?.id === order.id, order)
      })
    }));
  }

  @Action(CreatedOrderByWS)
  createdOrderByWS(ctx: StateContext<OrderListStateModel>, { order }: CreatedOrderByWS): void {

    const statusIds = this.store.selectSnapshot(OrderListState.orderStatusList);
    const allowedStatusIds = statusIds.filter(status => {
      return [OrderStatus.DONE, OrderStatus.ISSUED, OrderStatus.ISSUED_SELF, OrderStatus.NOT_CONFIRMED, OrderStatus.CANCELLED].includes(status.status);
    }).map(status => status.id);

    const orderList = ctx.getState().orderListInteractive.data;
    const potentialOrder = orderList.find(o => o.id === order.id);

    ctx.setState(patch({
      orderList: patch({
        data: insertItem(order, 0),
        meta: patch({
          totalItems: ctx.getState().orderList?.meta?.totalItems + 1
        })
      })
    }));

    if (allowedStatusIds.includes(order.status?.id)) {
      const isCurrentDay = order.createdAt.toString().split('T')[0] === new Date().toISOString().split('T')[0];
      if (!potentialOrder && isCurrentDay) {
        ctx.setState(patch({
          orderListInteractive: patch({
            data: insertItem(order, 0),
            meta: patch({
              totalItems: ctx.getState().orderListInteractive?.meta?.totalItems + 1
            })
          })
        }));
      }
    }
  }

  @Action(GetOrdersCount)
  getOrdersCount(ctx: StateContext<OrderListStateModel>): void {
    this.ordersService.getOrdersCount().subscribe({
      next: ordersCount => ctx.patchState({ ordersCount }),
      error: err => {
        console.error('Error fetching orders count:', err);

      }
    });
  }

  @Action(OrdersCountWs)
  ordersCountWs(ctx: StateContext<OrderListStateModel>, { ordersCount }: OrdersCountWs): void {
    ctx.patchState({ ordersCount });
  }

  @Action(AcceptedCountWS)
  acceptedCountWS(ctx: StateContext<OrderListStateModel>, { ordersCount }: AcceptedCountWS): void {
    ctx.setState(patch({
      ordersCount: patch({
        acceptedCount: ordersCount
      })
    }));
  }

  @Action(OrdersAcceptedCountWS)
  ordersAcceptedCountWS(ctx: StateContext<OrderListStateModel>, { acceptedCount }: OrdersAcceptedCountWS): void {
    ctx.setState(patch({
      ordersCount: patch({
        acceptedCount
      })
    }));
  }
}


